import { html, literal } from "lit/static-html.js";
import { customElement, property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";

import { emit } from "@/internals/events";
import { isEmptySlot } from "@/internals/slot";

import type { Theme } from "@/internals/theme";

import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";

import styles from "./atlas-toast.scss";

import "@/components/display/atlas-icon/atlas-icon";
import "@/components/display/atlas-text/atlas-text";
import "@/components/display/atlas-button/atlas-button";
import "@/components/display/atlas-icon-button/atlas-icon-button";

export type ToastProps = AtlasElementProps & {
    "title": string;
    "header-info": string;
    "icon": string;
    "icon-theme": Theme;
    "overlay-icon": string;
    "overlay-icon-theme": Theme;
    "href": string;
    "is-external-link": boolean;
    "has-action": boolean;
};

/**
 * @dependency atlas-icon
 * @dependency atlas-text
 * @dependency atlas-button
 * @dependency atlas-icon-button
 * 
 * @slot - O slot padrão é o conteúdo do toast
 * @slot actions - O slot de ações é onde podemos colocar botões de ação
 * 
 * @event {CustomEvent} atlas-toast-close - Evento lançado quando o toast é fechado
 * @event {CustomEvent} atlas-toast-click - Evento lançado quando o toast é clicado, versão clickable
 * 
 * @tag atlas-toast
 */
@customElement("atlas-toast")
export default class AtlasToast extends AtlasElement {
    static styles = styles;

    /** O título do toast */
    @property({ type: String }) header: string = "";

    /** O texto que será exibido ao lado do título */
    @property({ type: String, attribute: "header-info" }) headerInfo: string = "";

    /** O ícone que será exibido no toast */
    @property({ type: String }) icon: string;

    /** O tema do ícone que será exibido no toast */
    @property({ type: String, attribute: "icon-theme" }) iconTheme: Theme;

    /** O ícone que será exibido em cima do ícone do toast */
    @property({ type: String, attribute: "overlay-icon" }) overlayIcon: string;

    /** O tema do ícone que será exibido em cima do ícone do toast */
    @property({ type: String, attribute: "overlay-icon-theme" }) overlayIconTheme: Theme;

    /** URL para onde o usuário será redirecionado ao clicar no toast */
    @property({ type: String }) href: string;

    /** Indica se o toast é um link externo */
    @property({ type: Boolean, attribute: "is-external-link" }) isExternalLink: boolean = false;

    /** Indica se o toast tem ação de clique, para que o evento seja emitido ao clicar nela */
    @property({ type: Boolean, attribute: "has-action" }) hasAction: boolean = false;

    @state() private _hasSlottedActions: boolean = false;

    /**
     * Fecha o elemento do toast
     */
    public close() {
        emit(this, "atlas-toast-close");
    }

    private onToastClick() {
        emit(this, "atlas-toast-click");
    }

    private onToastCloseClick(event: MouseEvent) {
        event.preventDefault();
        this.close();
    }

    private onActionsSlotChange() {
        this._hasSlottedActions = !isEmptySlot(this, "actions");
    }

    private renderOverlayIcon() {
        const overlayIconClass = {
            "overlay-icon-container": true,
            [`${this.overlayIconTheme}`]: !!this.overlayIconTheme
        };

        return when(
            !!this.overlayIcon,
            () => html`
                <div class="${classMap(overlayIconClass)}">
                    <atlas-icon class="overlay-icon" name=${this.overlayIcon} white size="2x"></atlas-icon>
                </div>
            `
        );
    }

    private renderIcon() {
        const toastIconClass = {
            "toast-icon": true,
            "has-overlay-icon": !!this.overlayIcon
        };

        return when(
            !!this.icon,
            () => html`
                <div class="${classMap(toastIconClass)}">
                    <atlas-icon name="${this.icon}" theme="${this.iconTheme}" size="2x"></atlas-icon>
                    ${this.renderOverlayIcon()}
                </div>
            `
        );
    }

    private renderHeader() {
        return when(
            !!this.header,
            () => html`
                <div class="toast-header">
                    <atlas-text bold size="sm">${this.header}</atlas-text>
                    <atlas-text muted size="xs">${this.headerInfo}</atlas-text>
                </div>
            `
        );
    }

    private isInteractive() {
        return (!!this.href || this.hasAction) && !this._hasSlottedActions;
    }

    private getToastTag() {
        if (this._hasSlottedActions) {
            return literal`div`;
        }

        if (!!this.href) {
            return literal`a`;
        }

        if (this.hasAction) {
            return literal`button`;
        }

        return literal`div`;
    }

    /** @internal */
    public render() {
        const isInteractive = this.isInteractive();

        const toastClass = {
            "atlas-toast": true,
            "interactive": isInteractive
        };

        const toastActionsClass = {
            "toast-actions": true,
            "has-actions": this._hasSlottedActions
        };

        const tag = this.getToastTag();

        /* eslint-disable lit/binding-positions, lit/no-invalid-html */
        return html`
            <${tag}
                class="${classMap(toastClass)}"
                href=${ifDefined(this.href ? this.href : undefined)}
                rel="${ifDefined(this.href && this.isExternalLink ? "noreferrer" : undefined)}"
                target="${ifDefined(this.href && this.isExternalLink ? "_blank" : undefined)}"
                @click=${isInteractive ? this.onToastClick : undefined}
            >
                ${this.renderIcon()}
                <div class="toast-info">
                    ${this.renderHeader()}
                    <atlas-text size="sm">
                        <slot></slot>
                    </atlas-text>
                    <div class="${classMap(toastActionsClass)}">
                        <slot name="actions" @slotchange=${this.onActionsSlotChange}></slot>
                    </div>
                </div>
                <atlas-icon-button
                    icon="x"
                    theme="secondary"
                    class="toast-close"
                    size="2x"
                    @click=${this.onToastCloseClick}
                    theme-on-interactions
                ></atlas-icon-button>
            </${tag}>
        `;
        /* eslint-enable lit/binding-positions, lit/no-invalid-html */
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-toast": AtlasToast;
    }
}
